import React from "react";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import TravelerTypeFields from "./TravelerTypeFields";

/**
 * Per-traveler checkbox list + expandable per-traveler detail panel for
 * a segment. Shared between AddSegmentDialog and EditSegmentDialog so
 * the "select travelers on this segment" UX lives in exactly one place.
 *
 * Renders nothing when no travelers exist on the trip — the parent
 * dialog used to guard with `{travelers.length > 0 && (...)}`; that
 * guard is now baked in here for clarity.
 *
 * Props:
 *   travelers              — Trip travelers list. Each row has at least
 *                            { id, name }. Pass the unfiltered list.
 *   selectedIds            — formData.traveler_ids on the form state.
 *   travelerDetails        — formData.traveler_details on the form state.
 *   segmentType            — formData.type. Drives which per-traveler
 *                            fields TravelerTypeFields renders.
 *   expandedTravelers      — Set<string> of traveler ids that have their
 *                            details panel open. Owned by the parent so
 *                            "Apply to all" or "Save" don't blow it away.
 *   onToggleSelected       — (travelerId, checked) → void. Adds or
 *                            removes the traveler from the segment.
 *   onToggleExpanded       — (travelerId) → void. Toggles the per-pax
 *                            details panel.
 *   onUpdateTravelerDetail — (travelerId, field, value) → void. Used by
 *                            the nested <TravelerTypeFields>.
 */
export const SegmentTravelerSelection = ({
  travelers,
  selectedIds,
  travelerDetails,
  segmentType,
  expandedTravelers,
  onToggleSelected,
  onToggleExpanded,
  onUpdateTravelerDetail,
}) => {
  if (!Array.isArray(travelers) || travelers.length === 0) return null;

  return (
    <div className="grid gap-2">
      <Label>Travelers on this Segment</Label>
      <div className="space-y-3">
        {travelers.map((traveler) => {
          const isSelected = (selectedIds || []).includes(traveler.id);
          const isExpanded = expandedTravelers && expandedTravelers.has(traveler.id);
          const travelerDetail = (travelerDetails || []).find(
            (td) => td.traveler_id === traveler.id,
          );

          return (
            <div
              key={traveler.id}
              className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-900/40"
              data-testid={`segment-traveler-row-${traveler.id}`}
            >
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onToggleSelected(traveler.id, e.target.checked)}
                  className="rounded"
                  data-testid={`segment-traveler-checkbox-${traveler.id}`}
                />
                <span className="text-sm font-medium flex-1">{traveler.name}</span>
                {isSelected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleExpanded(traveler.id)}
                    className="h-6 w-6 p-0"
                    data-testid={`segment-traveler-expand-${traveler.id}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              {/* Expandable per-traveler details — context-aware on segment type */}
              {isSelected && isExpanded && travelerDetail && (
                <div className="mt-3 pl-6 space-y-3 border-l-2 border-sky-300 dark:border-sky-700">
                  <TravelerTypeFields
                    traveler={traveler}
                    travelerDetail={travelerDetail}
                    segmentType={segmentType}
                    updateTravelerDetail={onUpdateTravelerDetail}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentTravelerSelection;
